<template>
    <UIDialog v-if="isOpen" :open="isOpen" @update:open="handleOpenChange">
        <DialogContent class="max-w-md">
            <DialogHeader>
                <DialogTitle>{{ modalTitle }}</DialogTitle>
                <DialogDescription>
                    {{ modalDescription}}
                </DialogDescription>
            </DialogHeader>

            <div class="space-y-4">

                <div class="space-y-2">
                    <label class="text-sm font-medium text-slate-700" for="saved-search-name">
                    </label>
                    <Input
                        id="saved-search-name"
                        ref="inputElement"
                        v-model="valueInput"
                        :placeholder=modalPlaceholder
                        :class="{ 'border-red-500 ring-red-500': errorMessage }"
                        autofocus
                        @input="resetError"
                    />
                    <p v-if="errorMessage" class="text-xs text-red-700">
                        {{ errorMessage }}
                    </p>
                </div>
            </div>

            <DialogFooter>
                <Button type="button" variant="outline" @click="handleCancel">
                    Cancel
                </Button>
                <Button
                    type="button"
                    :disabled="!inputIsValid"
                    @click="handleSave()"
                >
                    Save
                </Button>
            </DialogFooter>
        </DialogContent>
    </UIDialog>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import {
    Dialog as UIDialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

const props = defineProps({
    isOpen: {
        type: Boolean,
        required: true,
    },
    onClose: {
        type: Function,
        required: true,
    },
    onSave: {
        type: Function,
        required: true,
    },
    onCancel: {
        type: Function,
        required: true,
    },
    initalText: {
        type: String,
        required: false,
    },
    title: {
        type: String,
        required: false,
    },
    description: {
        type: String,
        required: false,
    },
    placeholder : {
        type: String,
        required: false,
    },
    minLength : {
        type: Number,
        required: false,
        default: 1
    },
    maxLength : {
        type: Number,
        required: false,
        default: 100
    }
})

const inputElement = ref(null)
const valueInput = ref('')
const textValue = ref('')
const saveError = ref('')
const errorMessage = ref('')

const modalTitle = computed(() => props.title || 'Enter text')
const modalDescription = computed(() => props.description || 'Please enter text here')
const modalPlaceholder = computed(() => props.placeholder || '')

const inputIsValid = computed(() => {
    return valueInput.value && valueInput.value.length >= props.minLength && valueInput.value.length <= props.maxLength
})

const resetError = () => {
    saveError.value = ''
}

const resetState = () => {
    textValue.value = ''
    resetError()
}

const handleClose = () => {
    resetState()
    props.onClose()
}

const handleOpenChange = (open) => {
    if (!open) {
        handleClose()
    }
}

const handleSave = async () => {
    props.onSave(valueInput.value)
    handleClose()
}

const handleCancel = () => {
    resetState()
    props.onCancel()
}

watch(
    () => props.isOpen,
    async (newValue) => {
        if (newValue) {
            valueInput.value = props.initalText
            await nextTick()
            const el = inputElement.value?.$el
            if (el) {
                el.focus()
                el.setSelectionRange(el.value.length, el.value.length)
            }
        }
    },
    { immediate: true },
)

</script>
